import React, { useState, useEffect, useRef } from 'react';
import {
    createTest,
    updateTest,
    replaceTestQuestions,
    addTestsToVacancy,
    createTestEditorDraft,
    saveTestEditorDraft,
    publishTestEditorDraft,
    discardTestEditorDraft,
    uploadTestConsentDocument,
    deleteTestConsentDocument,
} from "../../services/testApi";
import {getVacancies} from "../../services/vacancyApi";
import {generateTest} from "../../services/aiApi";
import {TEST_TYPES, RESULT_TYPES} from "../../config/api";
import {useAlertContext} from "../../context/AlertContext";
import TestAiGenerator from "./TestAiGenerator";
import { QuestionContent, RichQuestionEditor } from "./TestQuestionContent";
import {
    emptyFormData,
    newQuestion,
    normalizeGeneratedTest,
    normalizeQuestions,
    questionsPayload,
    validateTestEditor,
} from "./testEditorModel";

export const testDraftKey = (testId) => `test-editor-draft:v1:${testId || "new"}`;

export const readTestDraft = (key) => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || "null");
        if (!parsed || typeof parsed !== "object" || !parsed.formData || !Array.isArray(parsed.questions)) {
            return null;
        }
        const formData = parsed.formData && typeof parsed.formData === "object" && !Array.isArray(parsed.formData)
            ? emptyFormData(parsed.formData)
            : emptyFormData();
        const aiFormData = parsed.aiFormData && typeof parsed.aiFormData === "object" && !Array.isArray(parsed.aiFormData)
            ? {
                topic: String(parsed.aiFormData.topic || ""),
                vacancy_id: String(parsed.aiFormData.vacancy_id || ""),
            }
            : { topic: "", vacancy_id: "" };
        return {
            ...parsed,
            formData,
            questions: normalizeQuestions(parsed.questions),
            mode: parsed.mode === "ai" ? "ai" : "manual",
            aiFormData,
            generatedVacancyIds: Array.isArray(parsed.generatedVacancyIds)
                ? parsed.generatedVacancyIds.filter((value) => value != null).map(String)
                : [],
            savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : null,
        };
    } catch {
        return null;
    }
};

export { validateTestEditor } from "./testEditorModel";

const TestForm = ({ initialData, onClose, onTestAdded, onDraftCreated, isAiTest = false, allowAi = false, pageMode = false }) => {
    const aiAvailable = Boolean(isAiTest || allowAi);
    const initialDraftKey = testDraftKey(initialData?.id);
    const draftKeyRef = useRef(initialDraftKey);
    const [initialDraft] = useState(() => readTestDraft(initialDraftKey));
    const [formData, setFormData] = useState(() => initialDraft?.formData || emptyFormData(initialData));
    const {showAlert} = useAlertContext();
    const [mode, setMode] = useState(initialDraft?.mode || "manual");
    const [vacancies, setVacancies] = useState([]);
    const [aiFormData, setAIFormData] = useState(initialDraft?.aiFormData || {
        topic: "",
        vacancy_id: "",
    })
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [consentUploading, setConsentUploading] = useState(false);
    const consentInputRef = useRef(null);
    const [serverTestId, setServerTestId] = useState(initialData?.id || null);
    const [serverSaveState, setServerSaveState] = useState("idle");
    const [serverSavedAt, setServerSavedAt] = useState(initialData?.updated_at || null);
    const [serverError, setServerError] = useState("");
    const [previewing, setPreviewing] = useState(false);
    const [editingTest] = useState(initialData);
    const [questions, setQuestions] = useState(() => normalizeQuestions(
        initialDraft?.questions || initialData?.questions
    ));
    const [generatedVacancyIds, setGeneratedVacancyIds] = useState(initialDraft?.generatedVacancyIds || []);
    const [hasChanges, setHasChanges] = useState(Boolean(initialDraft || initialData?.has_unpublished_changes));
    const [draftSavedAt, setDraftSavedAt] = useState(initialDraft?.savedAt || null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [focusQuestionClientId, setFocusQuestionClientId] = useState(null);
    const savedSuccessfullyRef = useRef(false);
    const lastServerPayloadRef = useRef("");
    const serverTestIdRef = useRef(initialData?.id || null);
    const serverSaveQueueRef = useRef(Promise.resolve());

    const editorState = () => ({
        formData,
        questions: normalizeQuestions(questions),
    });

    const persistToServer = (state, force = false) => {
        const serialized = JSON.stringify(state);
        const save = async () => {
            const currentServerId = serverTestIdRef.current;
            if (!force && currentServerId && serialized === lastServerPayloadRef.current) {
                return currentServerId;
            }
            setServerSaveState("saving");
            setServerError("");
            try {
                const response = currentServerId
                ? await saveTestEditorDraft(currentServerId, state)
                : await createTestEditorDraft(state);
                const id = Number(response?.id || currentServerId);
                if (!currentServerId && id) {
                    serverTestIdRef.current = id;
                    const previousKey = draftKeyRef.current;
                    const nextKey = testDraftKey(id);
                    if (previousKey !== nextKey) {
                        const localDraft = localStorage.getItem(previousKey);
                        if (localDraft) localStorage.setItem(nextKey, localDraft);
                        localStorage.removeItem(previousKey);
                        draftKeyRef.current = nextKey;
                    }
                    setServerTestId(id);
                    onDraftCreated?.(id);
                }
                lastServerPayloadRef.current = serialized;
                setServerSavedAt(response?.updated_at || new Date().toISOString());
                setServerSaveState("saved");
                return id;
            } catch (error) {
                setServerSaveState("error");
                setServerError(error?.message || String(error));
                throw error;
            }
        };
        const queued = serverSaveQueueRef.current.catch(() => undefined).then(save);
        serverSaveQueueRef.current = queued;
        return queued;
    };


    useEffect(() => {
        const loadVacancies = async () => {
            try {
                const data = await getVacancies();
                setVacancies(Array.isArray(data) ? data : [])
            } catch (e) {
                console.error("Ошибка загрузки вакансий: ", e)
            }
        }
        loadVacancies()
    }, [])

    useEffect(() => {
        if (!hasChanges) return undefined;
        const persist = (updateIndicator) => {
            const savedAt = new Date().toISOString();
            try {
                localStorage.setItem(draftKeyRef.current, JSON.stringify({
                    formData,
                    questions,
                    mode,
                    aiFormData,
                    generatedVacancyIds,
                    savedAt,
                }));
                if (updateIndicator) setDraftSavedAt(savedAt);
            } catch (_) {
                // The editor remains usable if browser storage is unavailable.
            }
        };
        const timer = window.setTimeout(() => persist(true), 400);
        return () => {
            window.clearTimeout(timer);
            // Also persist synchronously when an SPA navigation unmounts the page
            // before the debounce has elapsed.
            if (!savedSuccessfullyRef.current) persist(false);
        };
    }, [aiFormData, formData, generatedVacancyIds, hasChanges, mode, questions]);

    useEffect(() => {
        if (!pageMode || !hasChanges || saving || ["saving", "error"].includes(serverSaveState)) return undefined;
        const state = editorState();
        const serialized = JSON.stringify(state);
        if (serverTestId && serialized === lastServerPayloadRef.current) return undefined;
        const timer = window.setTimeout(() => {
            persistToServer(state).catch(() => {
                // The local draft remains available and the toolbar exposes retry.
            });
        }, 1200);
        return () => window.clearTimeout(timer);
        // State fields are intentionally explicit: status updates must not start a save loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, questions, pageMode, hasChanges, saving, serverTestId, serverSaveState]);

    useEffect(() => {
        if (!hasChanges) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [hasChanges]);

    const markChanged = () => {
        setHasChanges(true);
        setServerSaveState((current) => current === "error" ? "idle" : current);
    };

    const requestClose = () => {
        if (hasChanges && !window.confirm("Выйти из редактора? Черновик останется сохранён в этом браузере.")) return;
        onClose?.();
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        markChanged();
    };

    const handleConsentFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            showAlert("Размер пользовательского соглашения не должен превышать 10 МБ", "error");
            return;
        }
        try {
            setConsentUploading(true);
            const id = await persistToServer(editorState(), true);
            const response = await uploadTestConsentDocument(id, file);
            setFormData((current) => ({
                ...current,
                consent_document: response?.state?.formData?.consent_document || null,
            }));
            setHasChanges(true);
            setServerSaveState("saved");
            showAlert("Пользовательское соглашение добавлено в черновик", "success");
        } catch (error) {
            showAlert(error?.message || "Не удалось загрузить пользовательское соглашение", "error");
        } finally {
            setConsentUploading(false);
        }
    };

    const handleConsentDelete = async () => {
        if (!serverTestIdRef.current || !window.confirm("Удалить пользовательское соглашение из черновика теста?")) return;
        try {
            setConsentUploading(true);
            const response = await deleteTestConsentDocument(serverTestIdRef.current);
            setFormData((current) => ({
                ...current,
                consent_document: response?.state?.formData?.consent_document || null,
            }));
            setHasChanges(true);
            setServerSaveState("saved");
            showAlert("Пользовательское соглашение удалено из черновика", "success");
        } catch (error) {
            showAlert(error?.message || "Не удалось удалить пользовательское соглашение", "error");
        } finally {
            setConsentUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateTestEditor(formData, questions);
        setValidationErrors(errors);
        if (errors.length) {
            showAlert("Проверьте обязательные поля формы", "error");
            window.scrollTo?.({ top: 0, behavior: "smooth" });
            return;
        }

        try {
            setSaving(true);
            if (pageMode) {
                const id = await persistToServer(editorState(), true);
                const result = await publishTestEditorDraft(id);
                savedSuccessfullyRef.current = true;
                localStorage.removeItem(draftKeyRef.current);
                setHasChanges(false);
                setServerSaveState("saved");
                setServerSavedAt(result?.updated_at || new Date().toISOString());
                showAlert(
                    result?.published_version > 1 ? "Тестирование обновлено" : "Тест опубликован",
                    "success"
                );
                onTestAdded?.(result);
                return;
            }
            const durationRaw = String(formData.duration_minutes || "").trim();
            const durationMinutes = durationRaw
                ? Math.max(1, Math.min(600, Number(durationRaw) || 0))
                : null;
            const saveBody = {
                name: formData.name,
                test_type: formData.test_type,
                results_type: formData.results_type,
                url: formData.url || null,
                description: formData.description || null,
                instruction_text: formData.instruction_text,
                duration_minutes:
                    formData.test_type === "Вопросно-ответная форма" ? durationMinutes || null : null,
                passing_score_percent: Number(formData.passing_score_percent) || 80,
            };

            let result
            if(editingTest?.id) {
                result = await updateTest(editingTest.id, saveBody)
                if (formData.test_type === "Вопросно-ответная форма") {
                    showAlert("Сохраняем вопросы...", "info")
                    await replaceTestQuestions(editingTest.id, questionsPayload(questions))
                }
            } else {
                result = await createTest(saveBody)

                const payload = questionsPayload(questions);
                if (payload.length > 0) {
                    showAlert("Добавляем вопросы...", "info")
                    await replaceTestQuestions(result.id, payload)
                }

                if (generatedVacancyIds.length > 0) {
                    showAlert("Привязываем к вакансиям...", "info");
                    for (const vacancyId of generatedVacancyIds) {
                        await addTestsToVacancy(Number(vacancyId), [result.id]);
                    }
                }
            }

            savedSuccessfullyRef.current = true;
            localStorage.removeItem(draftKeyRef.current);
            setHasChanges(false);
            showAlert("Тест сохранен", "success")
            onTestAdded?.(result)
        } catch (e) {
            console.error("Ошибка сохранения", e)
            showAlert(`Ошибка: ${e.message}`, "error")
        } finally {
            setSaving(false);
        }
    };

    const handleAddQuestion = () => {
        const question = newQuestion();
        setQuestions(prev => [...prev, question]);
        setFocusQuestionClientId(question.client_id);
        markChanged();
    };

    const handleRemoveQuestion = (index) => {
        const question = questions[index];
        const hasContent = Boolean(
            String(question?.question_text || "").trim()
            || String(question?.explanation || "").trim()
            || (question?.options || []).some((option) => String(option || "").trim())
        );
        if (hasContent && !window.confirm(`Удалить заполненный вопрос №${index + 1}?`)) return;
        setQuestions(prev => prev.filter((_, i) => i !== index));
        markChanged();
    };

    const handleQuestionChange = (index, field, value) => {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== index) return q;
            if (field === "answer_type") {
                const hasRules = (q.options || []).some(Boolean) || q.evaluation_mode !== "none";
                if (hasRules && !window.confirm("Сменить тип ответа? Варианты и правила оценки будут сброшены.")) return q;
                const choice = ["single", "multiple"].includes(value);
                return {
                    ...q,
                    answer_type: value,
                    options: choice ? ["", ""] : [],
                    allow_custom_answer: false,
                    custom_answer_label: "Другой вариант",
                    correct_option_indexes: [],
                    correct_option_index: null,
                    evaluation_mode: "none",
                    max_score: "",
                };
            }
            return { ...q, [field]: value };
        }));
        markChanged();
    };

    const handleAddOption = (questionIndex) => {
        setQuestions(prev => prev.map((q, i) =>
            i === questionIndex ? { ...q, options: [...(q.options || []), ""] } : q
        ));
        markChanged();
    };

    const handleRemoveOption = (questionIndex, optionIndex) => {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== questionIndex) return q;
            const options = q.options.filter((_, oi) => oi !== optionIndex);
            const correctIndexes = (q.correct_option_indexes || [])
                .filter((value) => value !== optionIndex)
                .map((value) => value > optionIndex ? value - 1 : value);
            let correct = q.correct_option_index;
            if (correct === optionIndex) correct = null;
            else if (correct != null && correct > optionIndex) correct = correct - 1;
            return {
                ...q,
                options,
                correct_option_indexes: correctIndexes,
                correct_option_index: correct,
            };
        }));
        markChanged();
    };

    const handleMoveOption = (questionIndex, optionIndex, direction) => {
        const targetIndex = optionIndex + direction;
        setQuestions((prev) => prev.map((question, index) => {
            if (index !== questionIndex || targetIndex < 0 || targetIndex >= question.options.length) return question;
            const options = [...question.options];
            [options[optionIndex], options[targetIndex]] = [options[targetIndex], options[optionIndex]];
            const correct_option_indexes = (question.correct_option_indexes || []).map((correctIndex) => {
                if (correctIndex === optionIndex) return targetIndex;
                if (correctIndex === targetIndex) return optionIndex;
                return correctIndex;
            });
            return {
                ...question,
                options,
                correct_option_indexes,
                correct_option_index: question.answer_type === "single"
                    ? (correct_option_indexes[0] ?? null)
                    : null,
            };
        }));
        markChanged();
    };

    const handleOptionChange = (questionIndex, optionIndex, value) => {
        setQuestions(prev => prev.map((q, i) =>
            i === questionIndex ? {
                ...q,
                options: q.options.map((opt, oi) => oi === optionIndex ? value : opt)
            } : q
        ));
        markChanged();
    };

    const handleCorrectToggle = (questionIndex, optionIndex) => {
        setQuestions((prev) => prev.map((question, index) => {
            if (index !== questionIndex) return question;
            const current = question.correct_option_indexes || [];
            const correct = question.answer_type === "single"
                ? [optionIndex]
                : current.includes(optionIndex)
                    ? current.filter((item) => item !== optionIndex)
                    : [...current, optionIndex];
            return {
                ...question,
                correct_option_indexes: correct,
                correct_option_index: question.answer_type === "single" ? optionIndex : null,
            };
        }));
        markChanged();
    };

    const handleFileRuleChange = (questionIndex, field, value) => {
        setQuestions((prev) => prev.map((question, index) => index === questionIndex
            ? { ...question, file_rules: { ...question.file_rules, [field]: value } }
            : question));
        markChanged();
    };

    const handleDuplicateQuestion = (index) => {
        setQuestions((prev) => {
            const copy = {
                ...prev[index],
                id: undefined,
                client_id: `question-${Date.now()}-copy`,
                options: [...(prev[index].options || [])],
                correct_option_indexes: [...(prev[index].correct_option_indexes || [])],
                attachments: [...(prev[index].attachments || [])],
            };
            return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
        });
        markChanged();
    };

    const handleInsertQuestion = (index) => {
        const question = newQuestion();
        setQuestions((prev) => [
            ...prev.slice(0, index + 1),
            question,
            ...prev.slice(index + 1),
        ]);
        setFocusQuestionClientId(question.client_id);
        markChanged();
    };

    const handleDiscardChanges = async () => {
        if (!serverTestId || !initialData?.published_version) return;
        if (!window.confirm("Отбросить все неопубликованные изменения? Отменить это действие нельзя.")) return;
        try {
            setSaving(true);
            const response = await discardTestEditorDraft(serverTestId);
            const state = response?.state || {};
            setFormData({ ...emptyFormData(), ...(state.formData || {}) });
            setQuestions(normalizeQuestions(state.questions));
            setHasChanges(false);
            localStorage.removeItem(draftKeyRef.current);
            lastServerPayloadRef.current = JSON.stringify(state);
            setServerSaveState("saved");
            showAlert("Неопубликованные изменения отброшены", "success");
        } catch (error) {
            showAlert(`Не удалось отбросить изменения: ${error.message || error}`, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleMoveQuestion = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= questions.length) return;
        setQuestions((prev) => {
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
        markChanged();
    };

    const handleAiGenerate = async (e) => {
        e.preventDefault()
        if (loading) return

        if(!aiFormData.topic.trim() || !aiFormData.vacancy_id) {
            showAlert("Заполните все поля", "error")
            return
        }

        try {
            setLoading(true)
            const selected = vacancies.find(v => String(v.id) === String(aiFormData.vacancy_id));

            if (!selected) throw new Error("Вакансия не найдена");

            const descriptionText = `
                Название вакансии: ${selected.name || ''}
                Описание: ${selected.description || 'не указано'}
                `.trim();

            showAlert("Генерируем тест с помощью AI...", "info")

            const generatedTest = await generateTest(aiFormData.topic.trim(), descriptionText)
            const normalized = normalizeGeneratedTest(generatedTest, formData);

            setFormData(normalized.formData)
            setQuestions(normalized.questions);
            setGeneratedVacancyIds([aiFormData.vacancy_id]);
            markChanged();
            setMode("manual")
            showAlert("Тест сгенерирован. Проверьте вопросы и опубликуйте его", "success")

        } catch (e) {
            console.error("Ошибка AI генерации", e)
            showAlert(`Ошибка: ${e.message}`, "error")
        } finally {
            setLoading(false)
        }
    }

    return (
        <form
            onSubmit={handleSubmit}
            className={`flex flex-col bg-white ${pageMode ? "rounded-3xl border border-slate-200/80 p-5 shadow-sm md:p-8" : "h-full rounded-2xl overflow-hidden p-6"}`}
            data-testid="test-editor-form"
        >
            {!pageMode && <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                        {editingTest ? "Редактировать тест" : "Создать тест"}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">Настройки и содержание теста</p>
                </div>
                <button type="button" onClick={requestClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>}

            {pageMode && (
                <div className="sticky top-0 z-20 -mx-5 -mt-5 mb-8 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur md:-mx-8 md:-mt-8 md:px-8">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="mr-auto">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                <span className={`h-2.5 w-2.5 rounded-full ${serverSaveState === "error" ? "bg-rose-500" : serverSaveState === "saving" ? "animate-pulse bg-amber-500" : "bg-emerald-500"}`} />
                                {serverSaveState === "saving" && "Сохраняем черновик…"}
                                {serverSaveState === "error" && "Не удалось сохранить черновик"}
                                {!["saving", "error"].includes(serverSaveState) && (serverSavedAt
                                    ? `Черновик сохранён ${new Date(serverSavedAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`
                                    : "Черновик будет сохранён автоматически")}
                            </div>
                            {serverError && <p className="mt-1 max-w-xl text-xs text-rose-600">{serverError}</p>}
                        </div>
                        {serverSaveState === "error" && (
                            <button type="button" onClick={() => persistToServer(editorState(), true).catch(() => {})} className="rounded-xl border border-rose-200 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50">
                                Повторить
                            </button>
                        )}
                        {initialData?.published_version > 0 && hasChanges && (
                            <button type="button" onClick={handleDiscardChanges} disabled={saving} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                Отбросить изменения
                            </button>
                        )}
                        <button type="button" onClick={() => setPreviewing((value) => !value)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#cda834] hover:bg-amber-50">
                            {previewing ? "Вернуться к редактору" : "Предпросмотр"}
                        </button>
                    </div>
                </div>
            )}

            {pageMode && !previewing && (
                <div className="mb-8 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-slate-950 p-4 text-white">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Формат</p>
                        <p className="mt-1 font-semibold">{formData.test_type || "Не выбран"}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Содержание</p>
                        <p className="mt-1 font-semibold text-slate-900">{questions.length} вопросов</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Черновик</p>
                        <p className="mt-1 font-semibold text-slate-900">
                            {draftSavedAt ? `Сохранён ${new Date(draftSavedAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}` : "Ожидает изменений"}
                        </p>
                    </div>
                </div>
            )}

            {initialDraft && (
                <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="test-draft-restored">
                    <span aria-hidden="true" className="mt-0.5">↻</span>
                    <div><strong>Черновик восстановлен.</strong> Можно продолжить с места, на котором вы остановились.</div>
                </div>
            )}

            {validationErrors.length > 0 && (
                <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4" role="alert">
                    <p className="font-semibold text-rose-900">Форму пока нельзя сохранить</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-rose-800">
                        {validationErrors.map((error) => <li key={error}>{error}</li>)}
                    </ul>
                </div>
            )}

            {previewing && (
                <section className="mx-auto max-w-3xl rounded-3xl border border-violet-100 bg-[#faf9fd] p-5 shadow-sm md:p-8" data-testid="test-editor-preview">
                    <div className="mb-6 border-b border-slate-200 pb-6">
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-violet-700">Предпросмотр кандидата</p>
                        <h2 className="mt-2 text-3xl font-bold text-slate-950">{formData.name || "Без названия"}</h2>
                        {formData.description && <p className="mt-3 leading-6 text-slate-600">{formData.description}</p>}
                        {formData.instruction_text && <div className="mt-5 rounded-2xl border border-violet-100 bg-violet-50 p-4 text-sm leading-6 text-violet-950">{formData.instruction_text}</div>}
                    </div>
                    <div className="space-y-5">
                        {questions.length === 0 && <p className="py-10 text-center text-slate-500">Добавьте вопросы, чтобы увидеть тест.</p>}
                        {questions.map((question, index) => (
                            <article key={question.client_id || index} className="rounded-2xl border border-slate-200 bg-white p-5">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Вопрос {index + 1}{question.required ? " · обязательный" : ""}</p>
                                <QuestionContent value={question.question_text || "Текст вопроса не заполнен"} className="mt-2 font-medium leading-6 text-slate-900" />
                                {question.explanation && <p className="mt-2 text-sm text-slate-500">{question.explanation}</p>}
                                {["single", "multiple"].includes(question.answer_type) && (
                                    <div className="mt-4 space-y-2">
                                        {(question.options || []).map((option, optionIndex) => (
                                            <div key={optionIndex} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                                                <span className={`h-4 w-4 border border-slate-300 ${question.answer_type === "single" ? "rounded-full" : "rounded"}`} />
                                                {option || `Вариант ${optionIndex + 1}`}
                                            </div>
                                        ))}
                                        {question.allow_custom_answer && (
                                            <div className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                                                <div className="flex items-center gap-3">
                                                    <span className={`h-4 w-4 border border-slate-300 ${question.answer_type === "single" ? "rounded-full" : "rounded"}`} />
                                                    {question.custom_answer_label || "Другой вариант"}
                                                </div>
                                                <div className="ml-7 mt-2 h-9 rounded-lg border border-slate-200 bg-slate-50" />
                                            </div>
                                        )}
                                    </div>
                                )}
                                {question.answer_type === "short_text" && <div className="mt-4 h-11 rounded-xl border border-slate-200 bg-slate-50" />}
                                {question.answer_type === "long_text" && <div className="mt-4 h-28 rounded-xl border border-slate-200 bg-slate-50" />}
                                {question.answer_type === "file" && <div className="mt-4 rounded-xl border-2 border-dashed border-slate-200 p-7 text-center text-sm text-slate-500">Загрузка файла</div>}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            <div className={`flex-1 space-y-6 ${previewing ? "hidden" : ""} ${pageMode ? "overflow-visible" : "overflow-y-auto pr-2"}`}>
                {aiAvailable && (
                    <TestAiGenerator
                        mode={mode}
                        value={aiFormData}
                        vacancies={vacancies}
                        loading={loading}
                        onModeChange={(nextMode) => { setMode(nextMode); markChanged(); }}
                        onChange={(value) => { setAIFormData(value); markChanged(); }}
                        onGenerate={handleAiGenerate}
                    />
                )}

                {(mode === "manual" || !aiAvailable) && (
                    <div className={pageMode ? "grid gap-6 xl:grid-cols-[minmax(380px,460px)_minmax(0,1fr)] xl:items-start" : "space-y-6"}>
                    <div className="space-y-6">
                        <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 space-y-4">
                            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Настройки теста</h3>
                            
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Название теста *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Тип теста *</label>
                                    <div className="relative">
                                        <select
                                            value={formData.test_type}
                                            onChange={(e) => handleChange('test_type', e.target.value)}
                                            className="w-full appearance-none border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all cursor-pointer disabled:opacity-50"
                                            required 
                                            disabled={isAiTest}
                                        >
                                            {TEST_TYPES.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-400">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Тип результата *</label>
                                    <div className="relative">
                                        <select
                                            value={formData.results_type}
                                            onChange={(e) => handleChange('results_type', e.target.value)}
                                            className="w-full appearance-none border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all cursor-pointer disabled:opacity-50"
                                            required 
                                            disabled={isAiTest}
                                        >
                                            {RESULT_TYPES.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-400">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Раздел</label>
                                <select value={formData.test_category || "professional"} onChange={(e) => handleChange("test_category", e.target.value)} data-testid="test-category" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834]">
                                    <option value="professional">Профессиональный тест</option>
                                    <option value="universal">Универсальное тестирование (для всех кандидатов, например анкета)</option>
                                </select>
                                <p className="mt-1 text-xs text-slate-500">Универсальные — без контроля честности; показываются в разделе «Универсальные тестирования».</p>
                            </div>

                            {!isAiTest && formData.test_type === "url" && (
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">URL теста *</label>
                                    <input
                                        type="url"
                                        value={formData.url}
                                        onChange={(e) => handleChange('url', e.target.value)}
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all"
                                        placeholder="https://example.com/test"
                                    />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Проходной результат (%)</label>
                                        <input type="number" min={80} max={100} value={formData.passing_score_percent}
                                            onChange={(e) => handleChange("passing_score_percent", e.target.value)}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all" />
                                        <p className="mt-1 text-xs text-slate-500">Порог для следующего этапа автоматической рассылки. Не ниже 80%.</p>
                                    </div>
                                </div>
                            )}

                            {formData.test_type === "Вопросно-ответная форма" && (
                                <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-3 xl:grid-cols-2">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                                            Лимит прохождения (минуты)
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={600}
                                            value={formData.duration_minutes}
                                            onChange={(e) => handleChange("duration_minutes", e.target.value)}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all"
                                            placeholder="Без ограничения"
                                            data-testid="test-duration-minutes"
                                        />
                                        <p className="mt-1 text-xs text-slate-500">Пустое поле — тест без автоматического завершения.</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                                            Проходной результат (%)
                                        </label>
                                        <input
                                            type="number"
                                            min={80}
                                            max={100}
                                            value={formData.passing_score_percent}
                                            onChange={(e) => handleChange("passing_score_percent", e.target.value)}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all"
                                            data-testid="test-passing-score-percent"
                                        />
                                        <p className="mt-1 text-xs text-slate-500">Порог для перехода к следующему тесту в рассылке. Не ниже 80% — меньшее значение сохранится как 80%.</p>
                                    </div>
                                    <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-slate-50 p-4">
                                        <input type="checkbox" className="mt-1 h-4 w-4 accent-[#cda834]" checked={Boolean(formData.track_total_time)} onChange={(event) => handleChange("track_total_time", event.target.checked)} />
                                        <span><strong className="block text-sm text-slate-800">Учитывать общее время</strong><span className="mt-1 block text-xs leading-5 text-slate-500">Показывать фактическую длительность попытки независимо от лимита.</span></span>
                                    </label>
                                    {formData.test_category !== "universal" && (
                                    <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-4 md:col-span-2 xl:col-span-2" data-testid="test-strict-integrity-block">
                                        <h4 className="text-sm font-bold text-slate-900">Честность и успех</h4>
                                        <p className="mt-1 text-xs leading-5 text-slate-500">Уходы со вкладки, время вне теста и копирование фиксируются во всех тестах. Здесь — что считать успехом и что делать при нарушении.</p>
                                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                            <label className="block text-sm text-slate-700">Что считается успехом
                                                <select value={formData.success_rule || "score"} onChange={(e) => handleChange("success_rule", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="test-success-rule">
                                                    <option value="score">Балл не ниже проходного</option>
                                                    <option value="hr_decision">Решение HR (балл — ориентир)</option>
                                                </select>
                                            </label>
                                            <label className="block text-sm text-slate-700">При нарушении
                                                <select value={formData.integrity_on_violation || (formData.strict_integrity ? "fail" : "pass_flagged")} onChange={(e) => handleChange("integrity_on_violation", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="test-on-violation">
                                                    <option value="fail">Не засчитывать → «не подходит: тест»</option>
                                                    <option value="pass_flagged">Засчитать с пометкой</option>
                                                    <option value="hr_review">Только отметить, решает HR</option>
                                                </select>
                                            </label>
                                        </div>
                                        <label className="mt-3 flex cursor-pointer items-start gap-3">
                                            <input type="checkbox" className="mt-1 h-4 w-4 accent-[#cda834]" checked={Boolean(formData.suspicious_is_violation)} onChange={(event) => handleChange("suspicious_is_violation", event.target.checked)} data-testid="test-suspicious-is-violation" />
                                            <span><strong className="block text-sm text-slate-800">Подозрительное поведение считать нарушением</strong><span className="mt-1 block text-xs leading-5 text-slate-500">Несколько уходов со вкладки, больше минуты вне теста, вставка текста.</span></span>
                                        </label>
                                        <label className="mt-3 flex cursor-pointer items-start gap-3">
                                            <input type="checkbox" className="mt-1 h-4 w-4 accent-[#cda834]" checked={Boolean(formData.strict_integrity)} onChange={(event) => handleChange("strict_integrity", event.target.checked)} data-testid="test-strict-integrity" />
                                            <span><strong className="block text-sm text-slate-800">Жёсткий режим прохождения</strong><span className="mt-1 block text-xs leading-5 text-slate-500">Копирование и вставка отключены, на компьютере — полный экран, при превышении лимитов тест завершается автоматически.</span></span>
                                        </label>
                                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                            <label className="block text-sm text-slate-700">Нарушение: уходов со страницы больше
                                                <input type="number" min={1} max={100} value={formData.integrity_max_tab_switches} onChange={(e) => handleChange("integrity_max_tab_switches", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="test-integrity-max-switches" />
                                            </label>
                                            <label className="block text-sm text-slate-700">Нарушение: вне теста дольше (минуты)
                                                <input type="number" min={1} max={60} value={Math.round(Number(formData.integrity_max_away_seconds || 300) / 60)} onChange={(e) => handleChange("integrity_max_away_seconds", String(Math.max(1, Number(e.target.value) || 5) * 60))} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="test-integrity-max-away" />
                                            </label>
                                        </div>
                                    </div>
                                    )}
                                </div>
                            )}

                            <div className="rounded-2xl border border-violet-100 bg-violet-50/60 p-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <h4 className="text-sm font-bold text-slate-900">Согласие на обработку персональных данных</h4>
                                        <p className="mt-1 text-xs leading-5 text-slate-600">
                                            Добавьте PDF, DOC или DOCX до 10 МБ. Перед началом теста кандидат увидит документ и подтвердит согласие.
                                        </p>
                                        {formData.consent_document && (
                                            <p className="mt-2 text-sm font-semibold text-violet-800" data-testid="consent-document-name">
                                                {formData.consent_document.filename}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex shrink-0 flex-wrap gap-2">
                                        <input ref={consentInputRef} type="file" className="hidden"
                                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={handleConsentFile} data-testid="consent-document-input" />
                                        <button type="button" disabled={consentUploading}
                                            onClick={() => consentInputRef.current?.click()}
                                            className="rounded-xl bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50">
                                            {consentUploading ? "Загрузка…" : formData.consent_document ? "Заменить соглашение" : "Добавить пользовательское соглашение"}
                                        </button>
                                        {formData.consent_document && (
                                            <button type="button" disabled={consentUploading} onClick={handleConsentDelete}
                                                className="rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                                                Удалить
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 space-y-4">
                            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Описание</h3>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Описание</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all min-h-[80px] disabled:opacity-50"
                                    placeholder="Краткое описание теста"
                                    disabled={isAiTest}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Текст инструкции (видит кандидат) *</label>
                                <textarea
                                    value={formData.instruction_text}
                                    onChange={(e) => handleChange('instruction_text', e.target.value)}
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all min-h-[100px] disabled:opacity-50"
                                    placeholder="Подробная инструкция по прохождению теста"
                                    required 
                                    disabled={isAiTest}
                                />
                            </div>
                        </div>
                    </div>

                        {formData.test_type === "Вопросно-ответная форма" && !isAiTest && (
                            <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Вопросы теста</h3>
                                    <button
                                        type="button"
                                        onClick={handleAddQuestion}
                                        className="text-[#cda834] hover:text-[#b8952b] text-sm font-semibold transition-colors"
                                    >
                                        + Добавить вопрос
                                    </button>
                                </div>
                                
                                {questions.length === 0 ? (
                                    <div className="text-center py-6 text-slate-500 border-2 border-dashed border-slate-200 rounded-xl">
                                        Нет вопросов. Нажмите «Добавить вопрос».
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {questions.map((question, qIndex) => (
                                            <div key={question.id ?? `new-${qIndex}`} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm relative group">
                                                <div className="flex flex-wrap justify-between items-start gap-3 mb-2">
                                                    <label className="block text-sm font-semibold text-slate-700">Вопрос #{qIndex + 1}</label>
                                                    <div className="flex items-center gap-1">
                                                        <button type="button" disabled={qIndex === 0} onClick={() => handleMoveQuestion(qIndex, -1)} className="rounded-lg px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Переместить выше">↑</button>
                                                        <button type="button" disabled={qIndex === questions.length - 1} onClick={() => handleMoveQuestion(qIndex, 1)} className="rounded-lg px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Переместить ниже">↓</button>
                                                        <button type="button" onClick={() => handleDuplicateQuestion(qIndex)} className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">Копировать</button>
                                                         <button type="button" onClick={() => handleInsertQuestion(qIndex)} className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">Вставить после</button>
                                                        <button type="button" onClick={() => handleRemoveQuestion(qIndex)} className="rounded-lg px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 hover:text-red-700">Удалить</button>
                                                    </div>
                                                </div>

                                                <RichQuestionEditor
                                                    value={question.question_text}
                                                    onChange={(value) => handleQuestionChange(qIndex, "question_text", value)}
                                                    autoFocus={focusQuestionClientId === question.client_id}
                                                />

                                                <div className="mb-4">
                                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Пояснение к вопросу</label>
                                                    <textarea value={question.explanation || ""} onChange={(event) => handleQuestionChange(qIndex, "explanation", event.target.value)} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#cda834] focus:ring-2 focus:ring-[#cda834]/20" placeholder="Необязательная подсказка или контекст" />
                                                </div>

                                                <div className="grid gap-4 border-t border-slate-100 pt-4 md:grid-cols-2">
                                                    <div>
                                                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Тип ответа</label>
                                                        <select value={question.answer_type || "short_text"} onChange={(event) => handleQuestionChange(qIndex, "answer_type", event.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#cda834] focus:ring-2 focus:ring-[#cda834]/20">
                                                            <option value="single">Один вариант</option>
                                                            <option value="multiple">Несколько вариантов</option>
                                                            <option value="short_text">Короткий текст</option>
                                                            <option value="long_text">Развернутый текст</option>
                                                            <option value="file">Загрузка файла</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Оценивание</label>
                                                        <select value={question.evaluation_mode || "none"} onChange={(event) => handleQuestionChange(qIndex, "evaluation_mode", event.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#cda834] focus:ring-2 focus:ring-[#cda834]/20">
                                                            <option value="none">Без оценки</option>
                                                            {["single", "multiple"].includes(question.answer_type) && <option value="auto">Автопроверка</option>}
                                                            <option value="manual">Ручная проверка</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {["single", "multiple"].includes(question.answer_type) && (
                                                    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <span className="text-xs font-semibold text-slate-500 uppercase">Варианты ответа</span>
                                                            <button type="button" onClick={() => handleAddOption(qIndex)} className="text-xs font-semibold text-[#a7831d] hover:text-slate-900">+ Добавить вариант</button>
                                                        </div>
                                                    <div className="space-y-2">
                                                        {(question.options || []).map((opt, oIndex) => (
                                                            <div key={oIndex} className="flex gap-2 items-center">
                                                                <label
                                                                    className="shrink-0 flex items-center gap-1 text-xs text-slate-500 cursor-pointer"
                                                                    title="Правильный ответ"
                                                                >
                                                                    <input
                                                                        type={question.answer_type === "multiple" ? "checkbox" : "radio"}
                                                                        name={`correct-${qIndex}`}
                                                                        disabled={question.evaluation_mode !== "auto"}
                                                                        checked={(question.correct_option_indexes || []).includes(oIndex)}
                                                                        onChange={() => handleCorrectToggle(qIndex, oIndex)}
                                                                        className="accent-[#cda834] disabled:opacity-30"
                                                                    />
                                                                 <div className="flex shrink-0">
                                                                     <button type="button" disabled={oIndex === 0} onClick={() => handleMoveOption(qIndex, oIndex, -1)} className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-25" aria-label={`Переместить вариант ${oIndex + 1} выше`}>↑</button>
                                                                     <button type="button" disabled={oIndex === question.options.length - 1} onClick={() => handleMoveOption(qIndex, oIndex, 1)} className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-25" aria-label={`Переместить вариант ${oIndex + 1} ниже`}>↓</button>
                                                                 </div>
                                                                    {question.evaluation_mode === "auto" ? "верный" : "вариант"}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={opt}
                                                                    onChange={e => handleOptionChange(qIndex, oIndex, e.target.value)}
                                                                    className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-slate-400 transition-colors"
                                                                    placeholder={`Вариант ${oIndex + 1}`}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveOption(qIndex, oIndex)}
                                                                    className="text-red-400 hover:text-red-600 px-2"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                     <div className="mt-4 border-t border-slate-200 pt-4">
                                                         <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                                                             <input
                                                                 type="checkbox"
                                                                 checked={Boolean(question.allow_custom_answer)}
                                                                 onChange={(event) => handleQuestionChange(qIndex, "allow_custom_answer", event.target.checked)}
                                                                 className="h-4 w-4 accent-[#cda834]"
                                                             />
                                                             Разрешить кандидату указать свой вариант
                                                         </label>
                                                         {question.allow_custom_answer && (
                                                             <label className="mt-3 block text-xs font-semibold text-slate-600">
                                                                 Подпись варианта
                                                                 <input
                                                                     type="text"
                                                                     value={question.custom_answer_label || ""}
                                                                     onChange={(event) => handleQuestionChange(qIndex, "custom_answer_label", event.target.value)}
                                                                     className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal"
                                                                     placeholder="Другой вариант"
                                                                 />
                                                             </label>
                                                         )}
                                                     </div>
                                                    <p className="mt-2 text-[11px] text-slate-400">
                                                        {question.evaluation_mode === "auto" ? "Полный балл начисляется только при точном совпадении." : "Включите автопроверку, чтобы задать правильные ответы."}
                                                    </p>
                                                    </div>
                                                )}

                                                {question.answer_type === "file" && (
                                                    <div className="mt-5 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 md:grid-cols-3">
                                                        <label className="text-xs font-semibold text-slate-600">Форматы<input value={(question.file_rules?.formats || []).join(", ")} onChange={(event) => handleFileRuleChange(qIndex, "formats", event.target.value.split(",").map((item) => item.trim()).filter(Boolean))} className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal" /></label>
                                                        <label className="text-xs font-semibold text-slate-600">Максимум файлов<input type="number" min={1} max={5} value={question.file_rules?.max_files || 5} onChange={(event) => handleFileRuleChange(qIndex, "max_files", Number(event.target.value))} className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal" /></label>
                                                        <label className="text-xs font-semibold text-slate-600">Размер одного файла, МБ<input type="number" min={1} max={20} value={question.file_rules?.max_size_mb || 20} onChange={(event) => handleFileRuleChange(qIndex, "max_size_mb", Number(event.target.value))} className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal" /></label>
                                                    </div>
                                                )}

                                                {question.evaluation_mode !== "none" && (
                                                    <div className="mt-5 grid gap-4 md:grid-cols-[180px,1fr]">
                                                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Максимальный балл<input type="number" min={1} max={1000} value={question.max_score || ""} onChange={(event) => handleQuestionChange(qIndex, "max_score", event.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-normal" /></label>
                                                        {question.evaluation_mode === "manual" && <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Критерии ручной проверки<textarea value={question.evaluation_criteria || ""} onChange={(event) => handleQuestionChange(qIndex, "evaluation_criteria", event.target.value)} rows={3} className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-normal" placeholder="Например: 1 балл за обнаружение ошибки…" /></label>}
                                                    </div>
                                                )}

                                                <div className="mt-5 flex flex-wrap gap-5 border-t border-slate-100 pt-4">
                                                    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={Boolean(question.required)} onChange={(event) => handleQuestionChange(qIndex, "required", event.target.checked)} className="h-4 w-4 accent-[#cda834]" />Обязательный вопрос</label>
                                                    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={Boolean(question.track_time)} onChange={(event) => handleQuestionChange(qIndex, "track_time", event.target.checked)} className="h-4 w-4 accent-[#cda834]" />Учитывать время ответа</label>
                                                </div>
                                            </div>
                                        ))}
                                        <button type="button" onClick={handleAddQuestion} className="w-full rounded-2xl border-2 border-dashed border-[#cda834]/50 bg-amber-50/50 px-5 py-4 text-sm font-semibold text-[#8d6d13] transition hover:border-[#cda834] hover:bg-amber-50">+ Добавить вопрос в конец</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className={`flex flex-wrap items-center justify-end gap-3 mt-6 pt-6 border-t border-slate-100 ${pageMode ? "sticky bottom-4 z-10 rounded-2xl border border-slate-200 bg-white/95 px-4 pb-4 shadow-lg backdrop-blur" : ""}`}>
                <div className="mr-auto hidden text-xs text-slate-500 sm:block">
                    {initialData?.published_version > 0 && hasChanges
                        ? "Есть неопубликованные изменения — кандидаты пока видят предыдущую версию"
                        : hasChanges ? "Изменения сохраняются в черновик автоматически" : "Все изменения опубликованы"}
                </div>
                <button type="button" onClick={requestClose} disabled={saving} className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50">
                    Отмена
                </button>
                <button 
                    type="submit" 
                    disabled={(mode === "ai" && aiAvailable) || saving || loading}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-slate-900 rounded-xl hover:bg-slate-800 shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="test-editor-save"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {saving
                        ? "Публикуем…"
                        : initialData?.published_version > 0
                            ? "Обновить тестирование"
                            : "Опубликовать тест"}
                </button>
            </div>
        </form>
    );
}

export default TestForm;
